import { useState, type ChangeEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Badge, Button, Card, CardBody, DataTable, EmptyState, Input, LoadingState, type Column } from "@agentnative/ui";
import { useAuthStore } from "../stores/auth";
import { useCurrentOrganizationId } from "../stores/currentOrg";
import { apiErrorMessage } from "../lib/api/client";
import { createApiToken, getTokenScopes, listApiTokens, revokeApiToken, type ApiToken } from "../lib/api/tokens";

export function TokensRoute() {
  const user = useAuthStore((s) => s.user);
  const organizationId = useCurrentOrganizationId();
  const queryClient = useQueryClient();

  const [name, setName] = useState("");
  const [picked, setPicked] = useState<string[]>([]);
  const [days, setDays] = useState("90");
  const [restrict, setRestrict] = useState(false);
  const [children, setChildren] = useState(false);
  // The plaintext exists only in this response; it lives in component state
  // for as long as the page is open and is never stored anywhere.
  const [shown, setShown] = useState<string | null>(null);

  const tokensQuery = useQuery({ queryKey: ["api-tokens"], queryFn: listApiTokens, enabled: !!user });
  const scopesQuery = useQuery({ queryKey: ["api-token-scopes"], queryFn: getTokenScopes, enabled: !!user });

  const createMutation = useMutation({
    mutationFn: () =>
      createApiToken({
        name,
        scopes: picked,
        org_ids: restrict && organizationId ? [organizationId] : [],
        include_children: restrict && children,
        expires_in_days: Number(days) || undefined,
      }),
    onSuccess: (resp) => {
      setShown(resp.token);
      setName("");
      setPicked([]);
      queryClient.invalidateQueries({ queryKey: ["api-tokens"] });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (id: string) => revokeApiToken(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["api-tokens"] }),
  });

  if (!user) {
    return <EmptyState title="Not signed in" description="Sign in to manage API tokens." />;
  }

  const scopes = scopesQuery.data?.scopes ?? {};
  const columns: Column<ApiToken>[] = [
    { key: "name", label: "Name", render: (t) => <span className="font-medium">{t.name}</span> },
    {
      key: "state",
      label: "State",
      render: (t) =>
        t.revoked_at ? (
          <Badge variant="danger">Revoked</Badge>
        ) : new Date(t.expires_at) < new Date() ? (
          <Badge variant="warning">Expired</Badge>
        ) : (
          <Badge variant="success">Active</Badge>
        ),
    },
    { key: "scopes", label: "Scopes", render: (t) => <span className="font-mono text-xs">{t.scopes.join(", ")}</span> },
    {
      key: "orgs",
      label: "Organizations",
      render: (t) =>
        t.org_ids.length === 0 ? "All you belong to" : `${t.org_ids.length} restricted${t.include_children ? " + children" : ""}`,
    },
    { key: "expires", label: "Expires", render: (t) => new Date(t.expires_at).toLocaleDateString() },
    { key: "used", label: "Last used", render: (t) => (t.last_used_at ? new Date(t.last_used_at).toLocaleString() : "never") },
    {
      key: "actions",
      label: "",
      render: (t) =>
        t.revoked_at ? null : (
          <Button variant="danger" size="sm" disabled={revokeMutation.isPending} onClick={() => revokeMutation.mutate(t.id)}>
            Revoke
          </Button>
        ),
    },
  ];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">API tokens</h1>
      <p className="text-sm text-[var(--color-neutral-500)]">
        Tokens let an agent or script act as you with only the permissions you pick. They can never do more than you can,
        expire, and can be revoked at any time. A token cannot create or manage tokens.
      </p>

      {shown && (
        <Alert variant="warning" title="Copy this token now. It cannot be shown again.">
          <div className="mt-2 break-all rounded bg-[var(--color-neutral-100)] p-2 font-mono text-sm">{shown}</div>
          <div className="mt-2">
            <Button size="sm" onClick={() => setShown(null)}>
              I have copied it
            </Button>
          </div>
        </Alert>
      )}

      <Card padding="lg">
        <CardBody>
          <h2 className="mb-3 text-lg font-semibold">Create a token</h2>
          <div className="space-y-4">
            <Input label="Name" value={name} onChange={(e: ChangeEvent<HTMLInputElement>) => setName(e.target.value)} />
            <fieldset>
              <legend className="mb-1 text-sm font-medium">Permissions</legend>
              {Object.entries(scopes).map(([scope, desc]) => (
                <label key={scope} className="flex items-start gap-2 py-1 text-sm">
                  <input
                    type="checkbox"
                    checked={picked.includes(scope)}
                    onChange={(e) => setPicked(e.target.checked ? [...picked, scope] : picked.filter((s) => s !== scope))}
                  />
                  <span>
                    <span className="font-mono text-xs">{scope}</span> {desc}
                  </span>
                </label>
              ))}
            </fieldset>
            <Input
              label={`Expires in days (default ${scopesQuery.data?.default_ttl_days ?? 90}, max ${scopesQuery.data?.max_ttl_days ?? 365})`}
              value={days}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setDays(e.target.value)}
            />
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={restrict} disabled={!organizationId} onChange={(e) => setRestrict(e.target.checked)} />
              Restrict to the current organization
            </label>
            {restrict && (
              <label className="flex items-center gap-2 pl-6 text-sm">
                <input type="checkbox" checked={children} onChange={(e) => setChildren(e.target.checked)} />
                Include its child organizations
              </label>
            )}
            <Button disabled={createMutation.isPending || name.trim() === "" || picked.length === 0} onClick={() => createMutation.mutate()}>
              Create token
            </Button>
            {createMutation.isError && (
              <Alert variant="danger" title="Could not create the token">
                {apiErrorMessage(createMutation.error)}
              </Alert>
            )}
          </div>
        </CardBody>
      </Card>

      {tokensQuery.isLoading ? (
        <LoadingState message="Loading tokens..." />
      ) : tokensQuery.isError ? (
        <Alert variant="danger" title="Failed to load tokens">
          {apiErrorMessage(tokensQuery.error)}
        </Alert>
      ) : (tokensQuery.data ?? []).length === 0 ? (
        <EmptyState title="No tokens" description="Create one above to give an agent or script access." />
      ) : (
        <DataTable<ApiToken> data={tokensQuery.data!} columns={columns} emptyMessage="No tokens." />
      )}
    </div>
  );
}
